<script lang="ts">
  import { Trophy, RotateCcw } from 'lucide-svelte';
  import { fly } from 'svelte/transition';
  import Button from './ui/button.svelte';
  import Card from './ui/card.svelte';
  import type { Flashcard } from '../types/flashcard';
  import type {
    ExerciseSession,
    ExerciseItem,
    ExerciseResult,
    UserAnswer,
    EvaluationResult,
    ExerciseSessionConfig,
    SessionSummary,
    ExerciseMode,
  } from '../types/exercise';
  import {
    generateExerciseSession,
    generateExerciseSessionAsync,
    evaluateAnswer,
    calculateSessionSummary,
  } from '../lib/exercise-engine';
  import { supabase } from '../lib/supabase';
  import { xpActions } from '../stores/xp';
  import { XPService } from '../lib/xp-service';
  import { _ } from 'svelte-i18n';
  import { saveReview } from '../lib/fsrs-scheduler';
  import { Rating } from 'ts-fsrs';
  import { disabledExerciseModes } from '../stores/exerciseModes';

  // Exercise components
  import TypeInExercise from './exercises/TypeInExercise.svelte';
  import MultipleChoiceExercise from './exercises/MultipleChoiceExercise.svelte';
  import MatchPairsExercise from './exercises/MatchPairsExercise.svelte';
  import ListenTypeExercise from './exercises/ListenTypeExercise.svelte';
  import ClozeSmartExercise from './exercises/ClozeSmartExercise.svelte';
  import WordBuildExercise from './exercises/WordBuildExercise.svelte';
  import ExerciseResultsScreen from './exercises/ExerciseResultsScreen.svelte';

  interface Props {
    flashcards: Flashcard[];
    config?: Partial<ExerciseSessionConfig>;
    onBack: () => void;
  }

  let { flashcards, config = {}, onBack }: Props = $props();

  // Session state
  let session = $state<ExerciseSession | null>(null);
  let currentExercise = $derived(
    session ? session.exercises[session.currentIndex] : null
  );
  let progress = $derived(
    session ? ((session.currentIndex + 1) / session.exercises.length) * 100 : 0
  );

  // Answer state
  let currentAnswer = $state<UserAnswer | null>(null);
  let evaluationResult = $state<EvaluationResult | null>(null);
  let answerStartTime = $state(Date.now());
  let isSubmitting = $state(false);
  let showFeedback = $state(false);

  // User state
  let userId = $state<string | null>(null);
  let questionsShown = $state(0); // Count of questions shown for Enter hint

  // Get disabled modes from store
  let disabledModes = $derived.by(() => {
    let modes: ExerciseMode[] = [];
    disabledExerciseModes.subscribe((disabled) => {
      const now = Date.now();
      modes = disabled
        .filter((dm) => dm.disabledUntil > now)
        .map((dm) => dm.mode);
    })();
    return modes;
  });

  // Initialize session
  $effect(() => {
    if (!session) {
      // Load user ID first
      supabase.auth.getUser().then(async ({ data: { user } }) => {
        userId = user?.id ?? null;

        try {
          // Use async session generator if FSRS is enabled
          if (config.useFSRS) {
            console.log('🧠 Generating FSRS exercise session');
            session = await generateExerciseSessionAsync(
              flashcards.length > 0 ? flashcards : null,
              config,
              disabledModes
            );
            console.log('✅ FSRS session generated:', session?.exercises.length, 'exercises');
          } else {
            // Use regular session generator
            if (flashcards.length === 0) {
              console.warn('⚠️ No flashcards available!');
              return;
            }
            console.log('🚀 Generating exercise session with', flashcards.length, 'flashcards');
            session = generateExerciseSession(flashcards, config, disabledModes);
            console.log('✅ Session generated:', session?.exercises.length, 'exercises');
          }

          answerStartTime = Date.now();
        } catch (error) {
          console.error('❌ Error generating session:', error);
        }
      });

      // Load XP rewards
      xpActions.loadRewards().catch(console.error);
    }
  });

  // Handle answer submission from exercise components
  function handleAnswerChange(answer: UserAnswer) {
    currentAnswer = answer;
  }

  /**
   * Convert exercise result to FSRS rating
   *
   * Rating mapping:
   * - Again (1): Wrong answer or skipped
   * - Hard (2): Correct but with typo or low similarity (< 0.95)
   * - Good (3): Correct answer with good performance (>= 0.95 similarity)
   * - Easy (4): Perfect match (similarity = 1.0)
   */
  function convertToFSRSRating(result: ExerciseResult): Rating {
    if (!result.isCorrect || result.skipped) {
      return Rating.Again; // 1 = Wrong/Skipped
    }

    const similarity = result.evaluation?.similarityScore ?? 1.0;

    if (similarity === 1.0) {
      return Rating.Easy; // 4 = Perfect
    } else if (similarity >= 0.95) {
      return Rating.Good; // 3 = Good
    } else {
      return Rating.Hard; // 2 = Correct but had typo
    }
  }

  // Check the current answer
  async function checkAnswer() {
    if (!currentExercise || !currentAnswer || !session) return;

    isSubmitting = true;
    const timeTaken = Date.now() - answerStartTime;

    try {
      // Evaluate the answer
      const result = evaluateAnswer(
        currentExercise,
        currentAnswer,
        session.config.typoTolerance
      );
      evaluationResult = result;
      showFeedback = true;

      // Record the result
      const exerciseResult: ExerciseResult = {
        exerciseId: currentExercise.id,
        vocabItemId: currentExercise.vocabItem.id,
        mode: currentExercise.mode,
        isCorrect: result.isCorrect,
        userAnswer: result.normalizedUserAnswer,
        correctAnswer: result.correctAnswer,
        timeTaken,
        skipped: false,
        evaluation: result,
      };

      session.results.push(exerciseResult);

      // Award XP for correct answer
      if (result.isCorrect && userId) {
        XPService.addXP(userId, 'correct_answer', {
          flashcard_id: currentExercise.vocabItem.id,
          exercise_mode: currentExercise.mode,
        }).catch(console.error);
      }

      // Save FSRS review if enabled
      if (session.config.saveFSRSReview) {
        const rating = convertToFSRSRating(exerciseResult);
        console.log(`💾 Saving FSRS review: ${Rating[rating]} (${rating})`);

        saveReview(currentExercise.vocabItem.id, rating).catch((error) => {
          console.error('❌ Failed to save FSRS review:', error);
        });
      }
    } finally {
      isSubmitting = false;
    }
  }

  // Skip current exercise
  function skipExercise() {
    if (!currentExercise || !session) return;

    const timeTaken = Date.now() - answerStartTime;

    const exerciseResult: ExerciseResult = {
      exerciseId: currentExercise.id,
      vocabItemId: currentExercise.vocabItem.id,
      mode: currentExercise.mode,
      isCorrect: false,
      userAnswer: '',
      correctAnswer: currentExercise.correctAnswer,
      timeTaken,
      skipped: true,
      evaluation: {
        isCorrect: false,
        similarityScore: 0,
        correctAnswer: currentExercise.correctAnswer,
        normalizedUserAnswer: '',
        feedback: 'Přeskočeno',
        typoDetected: false,
      },
    };

    session.results.push(exerciseResult);
    evaluationResult = exerciseResult.evaluation;
    showFeedback = true;

    // Save FSRS review for skipped exercise
    if (session.config.saveFSRSReview) {
      const rating = convertToFSRSRating(exerciseResult);
      console.log(`💾 Saving FSRS review (skipped): ${Rating[rating]} (${rating})`);

      saveReview(currentExercise.vocabItem.id, rating).catch((error) => {
        console.error('❌ Failed to save FSRS review:', error);
      });
    }
  }

  // Move to next exercise
  function nextExercise() {
    if (!session) return;

    if (session.currentIndex < session.exercises.length - 1) {
      session.currentIndex++;
      resetExerciseState();
    } else {
      finishSession();
    }
  }

  // Reset state for new exercise
  function resetExerciseState() {
    currentAnswer = null;
    evaluationResult = null;
    showFeedback = false;
    answerStartTime = Date.now();
  }

  // Finish the session
  async function finishSession() {
    if (!session) return;

    session.isComplete = true;
    session.completedAt = new Date();

    // Save results to database
    await saveSessionResults();
  }

  // Save session results
  async function saveSessionResults() {
    if (!session || !userId) return;

    const summary = calculateSessionSummary(session);

    try {
      // Award XP based on session performance
      const testType =
        session.exercises.length <= 10
          ? 'short'
          : session.exercises.length >= 50
            ? 'long'
            : 'medium';

      await xpActions.awardTestXP(testType as 'short' | 'medium' | 'long', summary.scorePercentage);
    } catch (error) {
      console.error('Error saving session results:', error);
    }
  }

  // Restart session with same config
  function restartSession() {
    session = generateExerciseSession(flashcards, config);
    resetExerciseState();
  }

  // Handle keyboard Enter
  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      if (showFeedback) {
        nextExercise();
      } else if (currentAnswer) {
        checkAnswer();
      }
    }
  }
</script>

<svelte:window onkeydown={handleKeyDown} />

{#if session && !session.isComplete}
  <!-- Exercise in progress -->
  <div class="h-full bg-background flex flex-col overflow-hidden">
    <div class="container mx-auto px-4 py-3 max-w-md flex flex-col flex-1 overflow-hidden">
      <!-- Header with progress -->
      <div class="mb-3 flex-shrink-0">
        <div class="flex items-center justify-center mb-2">
          <div class="text-sm font-medium text-muted-foreground bg-secondary/30 px-3 py-1 rounded-full backdrop-blur-sm">
            {session.currentIndex + 1} / {session.exercises.length}
          </div>
        </div>

        <!-- Progress bar -->
        <div class="w-full bg-secondary/30 h-2 rounded-full overflow-hidden backdrop-blur-sm">
          <div
            class="bg-gradient-to-r from-primary to-accent h-full transition-all duration-500 ease-out shadow-[0_0_10px_rgba(var(--primary),0.5)]"
            style="width: {progress}%"
          ></div>
        </div>
      </div>

      {#if currentExercise}
        {#key currentExercise.id}
          <div
            in:fly={{ x: 50, duration: 400, delay: 150 }}
            out:fly={{ x: -50, duration: 300, opacity: 0 }}
            class="relative flex-1 flex flex-col overflow-hidden"
          >
          <!-- Mode badge -->
          <div class="flex justify-center mb-3 flex-shrink-0">
            <span class="px-4 py-1.5 text-xs font-semibold tracking-wider uppercase rounded-full bg-primary/10 text-primary border-2 border-primary/30 shadow-sm">
              {#if currentExercise.mode === 'TYPE_IN'}
                {$_('exercise.mode_type_in')}
              {:else if currentExercise.mode === 'MULTIPLE_CHOICE'}
                {$_('exercise.mode_multiple_choice')}
              {:else if currentExercise.mode === 'MATCH_PAIRS'}
                {$_('exercise.mode_match_pairs')}
              {:else if currentExercise.mode === 'LISTEN_TYPE'}
                {$_('exercise.mode_listen_type')}
              {:else if currentExercise.mode === 'CLOZE_SMART'}
                {$_('exercise.mode_cloze_smart')}
              {:else if currentExercise.mode === 'WORD_BUILD'}
                {$_('exercise.mode_word_build')}
              {/if}
            </span>
          </div>

          <!-- Exercise component based on mode -->
          <Card class="p-3 mb-3 bg-card/50 backdrop-blur-md border border-border/50 shadow-xl relative overflow-hidden flex-1">
             <!-- Decorative background blobs -->
            <div class="absolute -top-20 -right-20 w-40 h-40 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>
            <div class="absolute -bottom-20 -left-20 w-40 h-40 bg-accent/5 rounded-full blur-3xl pointer-events-none"></div>

            <div class="relative z-10">
              {#if currentExercise.mode === 'TYPE_IN'}
                <TypeInExercise
                  exercise={currentExercise}
                  {showFeedback}
                  {evaluationResult}
                  onAnswerChange={handleAnswerChange}
                />
              {:else if currentExercise.mode === 'MULTIPLE_CHOICE'}
                <MultipleChoiceExercise
                  exercise={currentExercise}
                  {showFeedback}
                  {evaluationResult}
                  onAnswerChange={handleAnswerChange}
                />
              {:else if currentExercise.mode === 'MATCH_PAIRS'}
                <MatchPairsExercise
                  exercise={currentExercise}
                  {showFeedback}
                  {evaluationResult}
                  onAnswerChange={handleAnswerChange}
                />
              {:else if currentExercise.mode === 'LISTEN_TYPE'}
                <ListenTypeExercise
                  exercise={currentExercise}
                  {showFeedback}
                  {evaluationResult}
                  onAnswerChange={handleAnswerChange}
                  onSkip={skipExercise}
                />
              {:else if currentExercise.mode === 'CLOZE_SMART'}
                <ClozeSmartExercise
                  exercise={currentExercise}
                  {showFeedback}
                  {evaluationResult}
                  onAnswerChange={handleAnswerChange}
                />
              {:else if currentExercise.mode === 'WORD_BUILD'}
                <WordBuildExercise
                  exercise={currentExercise}
                  {showFeedback}
                  {evaluationResult}
                  onAnswerChange={handleAnswerChange}
                />
              {/if}
            </div>
          </Card>

          <!-- Action buttons -->
          <div class="flex gap-3 flex-shrink-0">
            {#if !showFeedback}
              <Button onclick={skipExercise} variant="outline" class="flex-1 h-11 text-base hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 transition-all">
                {$_('exercise.skip')}
              </Button>
              <Button
                onclick={checkAnswer}
                disabled={!currentAnswer || isSubmitting}
                class="flex-1 h-11 text-base font-semibold shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all"
              >
                {isSubmitting ? $_('exercise.checking') : $_('exercise.check')}
              </Button>
            {:else}
              <Button onclick={nextExercise} class="flex-1 h-11 text-base font-semibold shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all">
                {session.currentIndex < session.exercises.length - 1
                  ? $_('exercise.next')
                  : $_('exercise.finish')}
              </Button>
            {/if}
          </div>
          </div>
        {/key}
      {/if}
    </div>
  </div>
{:else if session && session.isComplete}
  <!-- Results screen -->
  <ExerciseResultsScreen {session} {onBack} onRestart={restartSession} />
{:else if flashcards.length === 0}
  <!-- No flashcards error -->
  <div class="min-h-screen bg-background flex items-center justify-center">
    <div class="text-center max-w-md">
      <div class="text-6xl mb-4">📚</div>
      <h2 class="text-2xl font-bold mb-2">{$_('exercise.no_flashcards')}</h2>
      <p class="text-muted-foreground mb-6">{$_('exercise.no_flashcards_description')}</p>
      <Button onclick={onBack} variant="default">
        {$_('buttons.back')}
      </Button>
    </div>
  </div>
{:else}
  <!-- Loading -->
  <div class="min-h-screen bg-background flex items-center justify-center">
    <div class="text-center">
      <div class="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
      <p class="text-muted-foreground">{$_('exercise.loading_exercise')}</p>
    </div>
  </div>
{/if}
